import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, NotebookPen, Pencil, RotateCcw, Search, Trash2 } from 'lucide-react';
import type { LostDeal, PipelineMeta, Stage } from '@epasu/core';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DealDialog } from '@/components/DealDialog';
import { LostDealNotesDialog } from '@/components/LostDealNotesDialog';
import { ReopenDealDialog } from '@/components/ReopenDealDialog';
import { crm } from '@/lib/crm-client';
import { formatDate, formatEuro } from '@/lib/utils';

/**
 * Verlorene Deals über alle Pipelines hinweg — die Ansicht hinter dem Filter
 * „Verloren“ auf dem Akquise-Brett (`akquise/BrettTab.tsx`). Die Überschrift
 * trägt der Filter; hier bleibt die Zählzeile.
 *
 * Kein Friedhof, sondern eine Arbeitsliste: was einmal abgesagt wurde, ist der
 * beste Ausgangspunkt für die nächste Runde. Deshalb steht in jeder Zeile das,
 * was einen zweiten Anlauf trägt — die Adresse, der genannte Grund und wie
 * lange das her ist —, und der Weg zurück in die Pipeline ist ein Klick.
 *
 * Bearbeiten und Löschen gehören ebenfalls hierher: das Brett zeigt keine
 * Absagen mehr, also ist dies der einzige Ort, an dem sich ein abgesagter Deal
 * ändern lässt. Der Umweg über „Wieder aufnehmen“ und erneutes Absagen würde
 * `lostAt` und den Grund verlieren — genau das, wofür die Seite da ist.
 */
export function LostDealsPage() {
  const navigate = useNavigate();

  const [deals, setDeals] = useState<LostDeal[]>([]);
  const [pipes, setPipes] = useState<PipelineMeta[]>([]);
  const [pipelineId, setPipelineId] = useState<string>('');
  const [suche, setSuche] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [notizFuer, setNotizFuer] = useState<LostDeal | null>(null);
  const [reaktivieren, setReaktivieren] = useState<LostDeal | null>(null);
  const [stufen, setStufen] = useState<Stage[]>([]);
  const [bearbeiten, setBearbeiten] = useState<{ deal: LostDeal; stufen: Stage[] } | null>(
    null,
  );
  const [loeschen, setLoeschen] = useState<LostDeal | null>(null);

  const load = useCallback(async () => {
    try {
      setDeals(await crm.pipelines.lostDeals({ pipelineId: pipelineId || undefined }));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen');
    }
    setLoading(false);
  }, [pipelineId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void crm.pipelines.listWithMeta().then(setPipes).catch(() => setPipes([]));
  }, []);

  // Die Suche läuft in der Oberfläche, nicht am Server: die Liste ist
  // überschaubar, und so tippt es sich ohne Wartezeit.
  const gefiltert = useMemo(() => {
    const q = suche.trim().toLowerCase();
    if (!q) return deals;
    return deals.filter((d) =>
      [d.title, d.contactName, d.contactEmail, d.organizationName, d.notes]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [deals, suche]);

  const summe = useMemo(
    () => gefiltert.reduce((s, d) => s + d.valueCents, 0),
    [gefiltert],
  );
  const ohneNotiz = useMemo(
    () => gefiltert.filter((d) => !d.notes?.trim()).length,
    [gefiltert],
  );

  const oeffneReaktivieren = async (deal: LostDeal) => {
    setReaktivieren(deal);
    setStufen(await crm.pipelines.stages(deal.pipelineId));
  };

  const oeffneBearbeiten = async (deal: LostDeal) => {
    try {
      setBearbeiten({ deal, stufen: await crm.pipelines.stages(deal.pipelineId) });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">
            {gefiltert.length} {gefiltert.length === 1 ? 'Deal' : 'Deals'}
            {summe > 0 && ` · ${formatEuro(summe)} nicht zustande gekommen`}
            {ohneNotiz > 0 && ` · ${ohneNotiz} ohne Notiz`}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              placeholder="Firma, Person, Grund …"
              className="w-64 pl-9"
              aria-label="Verlorene Deals durchsuchen"
            />
          </div>

          <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
            <FilterButton active={pipelineId === ''} onClick={() => setPipelineId('')}>
              Alle
            </FilterButton>
            {pipes.map((p) => (
              <FilterButton
                key={p.id}
                active={pipelineId === p.id}
                onClick={() => setPipelineId(p.id)}
              >
                {p.audienceName || p.name}
              </FilterButton>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <Card className="border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {error}
        </Card>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Lade …</p>
      ) : gefiltert.length === 0 ? (
        <Card className="p-6 text-sm text-muted-foreground">
          {deals.length === 0
            ? 'Noch nichts verloren. Deals landen hier automatisch, sobald sie in der Pipeline auf „Verloren“ wandern.'
            : 'Kein Treffer zu dieser Suche.'}
        </Card>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Deal</TH>
              <TH>Kontakt</TH>
              <TH>Pipeline</TH>
              <TH>Wert</TH>
              <TH>Verloren</TH>
              <TH>Grund / Notiz</TH>
              <TH />
            </TR>
          </THead>
          <TBody>
            {gefiltert.map((deal) => (
              <TR key={deal.id}>
                <TD className="font-medium">{deal.title}</TD>

                <TD>
                  {deal.contactId ? (
                    <button
                      className="text-left hover:underline"
                      onClick={() => navigate(`/kontakte/${deal.contactId}`)}
                    >
                      {deal.contactName}
                    </button>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                  {deal.organizationName && (
                    <div className="text-xs text-muted-foreground">
                      {deal.organizationName}
                    </div>
                  )}
                  {deal.contactOptedOut && (
                    <div className="text-xs text-destructive">
                      abgemeldet — nicht anschreiben
                    </div>
                  )}
                </TD>

                <TD className="text-sm text-muted-foreground">
                  {deal.audienceName || deal.pipelineName}
                  <div className="text-xs">{deal.stageName}</div>
                </TD>

                <TD>{deal.valueCents ? formatEuro(deal.valueCents) : '—'}</TD>

                <TD className="whitespace-nowrap text-sm">
                  {deal.lostAt ? formatDate(deal.lostAt) : '—'}
                  {deal.lostAt && (
                    <div className="text-xs text-muted-foreground">
                      {verstrichen(deal.lostAt)}
                    </div>
                  )}
                </TD>

                <TD className="max-w-xs">
                  <button
                    className="text-left text-sm hover:underline"
                    onClick={() => setNotizFuer(deal)}
                    title="Notiz bearbeiten"
                  >
                    {deal.notes?.trim() ? (
                      <span className="line-clamp-2 whitespace-pre-wrap">{deal.notes}</span>
                    ) : (
                      <span className="text-muted-foreground">
                        <NotebookPen className="mr-1 inline h-3.5 w-3.5" />
                        Grund nachtragen
                      </span>
                    )}
                  </button>
                </TD>

                <TD>
                  <div className="flex justify-end gap-1">
                    {deal.contactEmail && !deal.contactOptedOut && (
                      <a
                        href={`mailto:${deal.contactEmail}`}
                        aria-label={`E-Mail an ${deal.contactEmail}`}
                        title={deal.contactEmail}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors hover:bg-accent hover:text-accent-foreground"
                      >
                        <Mail className="h-4 w-4" />
                      </a>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`${deal.title} bearbeiten`}
                      title="Bearbeiten"
                      onClick={() => void oeffneBearbeiten(deal)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`${deal.title} löschen`}
                      title="Löschen"
                      onClick={() => setLoeschen(deal)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void oeffneReaktivieren(deal)}
                    >
                      <RotateCcw className="mr-2 h-3.5 w-3.5" />
                      Wieder aufnehmen
                    </Button>
                  </div>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      {notizFuer && (
        <LostDealNotesDialog
          dealId={notizFuer.id}
          dealTitle={notizFuer.title}
          stageName={notizFuer.stageName}
          notes={notizFuer.notes}
          onClose={() => setNotizFuer(null)}
          onSaved={() => {
            setNotizFuer(null);
            void load();
          }}
        />
      )}

      {bearbeiten && (
        <DealDialog
          deal={bearbeiten.deal}
          stage={
            bearbeiten.stufen.find((st) => st.id === bearbeiten.deal.stageId) ??
            (bearbeiten.stufen[0] as Stage)
          }
          stages={bearbeiten.stufen}
          pipelineId={bearbeiten.deal.pipelineId}
          onClose={() => setBearbeiten(null)}
          onSaved={() => {
            setBearbeiten(null);
            void load();
          }}
        />
      )}

      {loeschen && (
        <ConfirmDialog
          title={`Deal „${loeschen.title}“ löschen?`}
          confirmLabel="Löschen"
          destructive
          onCancel={() => setLoeschen(null)}
          onConfirm={async () => {
            await crm.pipelines.deleteDeal(loeschen.id);
            setLoeschen(null);
            await load();
          }}
        >
          <p className="text-sm text-muted-foreground">
            Aufgaben zu diesem Deal bleiben bestehen, verlieren aber den Bezug.
          </p>
        </ConfirmDialog>
      )}

      {reaktivieren && (
        <ReopenDealDialog
          deal={reaktivieren}
          stages={stufen}
          onClose={() => setReaktivieren(null)}
          onMoved={() => {
            setReaktivieren(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
        active ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
      }`}
    >
      {children}
    </button>
  );
}

/** „vor 3 Monaten“ — grob genug, um beim Überfliegen zu helfen. */
function verstrichen(iso: string): string {
  const tage = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (tage < 1) return 'heute';
  if (tage === 1) return 'gestern';
  if (tage < 31) return `vor ${tage} Tagen`;
  const monate = Math.round(tage / 30);
  if (monate < 12) return `vor ${monate} ${monate === 1 ? 'Monat' : 'Monaten'}`;
  const jahre = Math.floor(tage / 365);
  return `vor ${jahre} ${jahre === 1 ? 'Jahr' : 'Jahren'}`;
}
